<script setup lang="ts">
import { computed, onMounted, watchEffect } from "vue"
import { useModal } from "@/composables"

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    name: string | symbol
    keepAlive?: boolean
    showByDefault?: boolean
    allowOverflow?: boolean
    closeOnBackdropClick?: boolean
    transition?: BaseModalTransition
    size?: BaseModalSize
  }>(),
  { keepAlive: false, showByDefault: false, allowOverflow: false, closeOnBackdropClick: false, transition: "fade", size: "default" },
)

const emit = defineEmits<{
  (e: "open"): void
  (e: "close"): void
}>()

export type BaseModalTransition = "fade" | "flip" | "flip-side" | "scale" | "slide-down" | "slide-up"
export type BaseModalSize = "default" | "lg" | "xl"

const panelWidthBySize: Record<BaseModalSize, string> = {
  default: "w-[768px]",
  lg: "w-[960px]",
  xl: "w-[1024px]",
}

const transitionName = computed(() =>
  props.transition === "fade" ? "gpx-fade" : `gpx-modal-${props.transition}`,
)

const needsPerspective = computed(
  () => props.transition === "flip" || props.transition === "flip-side",
)

const panelSizeClass = computed(() => panelWidthBySize[props.size])

const { toggle, isActive } = useModal(props.name)

function onBackdropClick() {
  if (props.closeOnBackdropClick)
    toggle()
}

onMounted(() => {
  if (props.showByDefault && !isActive?.value) {
    toggle()
  }
})

watchEffect(() => isActive?.value ? emit("open") : emit("close"))
</script>

<template>
  <teleport to="body">
    <Transition :name="transitionName">
      <div v-if="keepAlive || isActive" v-show="isActive" class="inset-0 h-full z-50 bg-black/40 fixed" data-test="base_modal">
        <div
          class="inset-0 w-full h-full flex justify-center items-center p-4"
          :class="{ 'perspective-[1000px]': needsPerspective }"
          @click.self="onBackdropClick"
        >
          <div
            class="max-w-[85vw] max-h-[85vh] overflow-y-auto"
            :class="[panelSizeClass, { 'overflow-y-visible': allowOverflow }]"
          >
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </teleport>
</template>

<style>
/* Non-fade variants: backdrop opacity on the root; panel is [data-test="base_modal"] > div > div (stable structure). */
.gpx-modal-slide-up-enter-active,
.gpx-modal-slide-up-leave-active,
.gpx-modal-slide-down-enter-active,
.gpx-modal-slide-down-leave-active,
.gpx-modal-scale-enter-active,
.gpx-modal-scale-leave-active,
.gpx-modal-flip-enter-active,
.gpx-modal-flip-leave-active,
.gpx-modal-flip-side-enter-active,
.gpx-modal-flip-side-leave-active {
  transition: opacity 0.25s ease;
}

.gpx-modal-slide-up-enter-from,
.gpx-modal-slide-up-leave-to,
.gpx-modal-slide-down-enter-from,
.gpx-modal-slide-down-leave-to,
.gpx-modal-scale-enter-from,
.gpx-modal-scale-leave-to,
.gpx-modal-flip-enter-from,
.gpx-modal-flip-leave-to,
.gpx-modal-flip-side-enter-from,
.gpx-modal-flip-side-leave-to {
  opacity: 0;
}

.gpx-modal-slide-up-enter-active[data-test="base_modal"] > div > div,
.gpx-modal-slide-up-leave-active[data-test="base_modal"] > div > div {
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
}

.gpx-modal-slide-up-enter-from[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: translateY(1.25rem);
}

.gpx-modal-slide-up-leave-to[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: translateY(0.75rem);
}

.gpx-modal-slide-down-enter-active[data-test="base_modal"] > div > div,
.gpx-modal-slide-down-leave-active[data-test="base_modal"] > div > div {
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
}

.gpx-modal-slide-down-enter-from[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: translateY(-1.25rem);
}

.gpx-modal-slide-down-leave-to[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: translateY(-0.75rem);
}

.gpx-modal-scale-enter-active[data-test="base_modal"] > div > div,
.gpx-modal-scale-leave-active[data-test="base_modal"] > div > div {
  transition:
    transform 0.28s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
}

.gpx-modal-scale-enter-from[data-test="base_modal"] > div > div,
.gpx-modal-scale-leave-to[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: scale(0.94);
}

.gpx-modal-flip-enter-active[data-test="base_modal"] > div > div,
.gpx-modal-flip-leave-active[data-test="base_modal"] > div > div {
  transition:
    transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
  transform-origin: center center;
}

.gpx-modal-flip-enter-from[data-test="base_modal"] > div > div,
.gpx-modal-flip-leave-to[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: rotateX(-14deg) translateZ(0);
}

.gpx-modal-flip-side-enter-active[data-test="base_modal"] > div > div,
.gpx-modal-flip-side-leave-active[data-test="base_modal"] > div > div {
  transition:
    transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
  transform-origin: center center;
}

.gpx-modal-flip-side-enter-from[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: rotateY(-16deg) translateZ(0);
}

.gpx-modal-flip-side-leave-to[data-test="base_modal"] > div > div {
  opacity: 0;
  transform: rotateY(12deg) translateZ(0);
}
</style>
